import { styled } from "@mui/material";
import { DataGridPremium, DataGridPremiumProps } from "@mui/x-data-grid-premium";
import { hexToRgbString } from "../../Utils/Helpers/cssUtils";

interface Props extends DataGridPremiumProps {
  noControls?: boolean;
}

const _DataGrid = styled(DataGridPremium, {
  shouldForwardProp: (prop) => prop !== "noControls" && prop !== "density",
})<Props>(({ theme, pagination, density, noControls }) => ({
  border: "none",

  "--DataGrid-containerBackground": theme.palette.background.default,
  "--DataGrid-pinnedBackground": theme.palette.background.default,
  "--DataGrid-rowBorderColor": theme.palette.backgroundAlpha.border,
  "--unstable_DataGrid-radius": 0,

  marginLeft: theme.spacing(-2),
  marginRight: theme.spacing(-2),

  ".MuiDataGrid-columnSeparator": {
    display: "none",
  },
  ".MuiDataGrid-columnHeaders, .MuiDataGrid-cell": {
    borderColor: theme.palette.backgroundAlpha.border,
    display: "flex",
    alignItems: "center",
  },
  ".MuiDataGrid-cell": {
    //   paddingTop: theme.spacing(1.5),
    //   paddingBottom: theme.spacing(1.5),
    ...(density === "standard" && {
      paddingTop: theme.spacing(1),
      paddingBottom: theme.spacing(1),
    }),
    ...(density === "comfortable" && {
      paddingTop: theme.spacing(2),
      paddingBottom: theme.spacing(2),
    }),
    paddingLeft: theme.spacing(2),
    paddingRight: 0,
    border: 0,

    "&.MuiDataGrid-cell--textCenter, &.MuiDataGrid-cell--textRight": {
      paddingRight: theme.spacing(2),
    },

    "&.MuiDataGrid-cellCheckbox": {
      paddingLeft: 0,
      paddingRight: 0,
    },
  },
  ".MuiDataGrid-cell:focus, .MuiDataGrid-cell:focus-within": {
    outline: "none",
  },

  // Column headers
  ".MuiDataGrid-columnHeaders": {
    // backgroundColor: theme.palette.background.paper,
    // borderBottom: "1px solid",
    // borderColor: theme.palette.divider,
    // borderRadius: "6px",
    ...theme.typography.body1,
    fontWeight: 400,
    color: theme.palette.text.secondary,
    borderBottom: `1px solid ${theme.palette.divider}`,
    alignItems: "flex-start",

    ".MuiDataGrid-columnHeader:not(.MuiDataGrid-columnHeaderCheckbox)": {
      border: 0,
      paddingLeft: theme.spacing(2),
      paddingRight: 0,

      "&.MuiDataGrid-columnHeader--alignCenter": {
        paddingRight: theme.spacing(2),
      },

      "&:focus, &:focus-within": {
        outline: "none",
      },

      ".MuiFormControl-root": {
        ".MuiFormLabel-root": {
          display: "none",
        },
        ".MuiOutlinedInput-root": {
          marginTop: 0,
        },
      },
    },

    ".MuiDataGrid-columnHeader--pinnedRight, .MuiDataGrid-columnHeader--pinnedLeft": {
      backgroundColor: theme.palette.background.default,
      // boxShadow: `2px 0px 8px 0px rgba(${hexToRgbString(theme.palette.background.paper)}, 1)`,
      backgroundImage: "none",
      // backdropFilter: "blur(4px)",
      borderRight: "0",
      borderLeft: "0",
    },

    ".MuiDataGrid-headerFilterRow": {
      paddingBottom: theme.spacing(2),
    },
  },
  ".MuiDataGrid-filler--pinnedRight, .MuiDataGrid-filler--pinnedLeft": {
    borderRight: 0,
    borderLeft: 0,
  },
  ".MuiDataGrid-columnHeaderTitle, .MuiDataGrid-columnHeaderTitleContainer": {
    ...theme.typography.body1,
    color: theme.palette.text.tier,
    fontWeight: 500,

    ".MuiDataGrid-iconButtonContainer": {
      marginLeft: theme.spacing(1),

      ".MuiButtonBase-root": {
        width: 16,
        height: 16,
        background: "transparent",

        "&:hover": {
          ".MuiSvgIcon-root": {
            opacity: 1,
          },
        },
      },
    },
  },

  ".MuiDataGrid-actionsCell": {
    gridGap: 0,
    color: theme.palette.text.secondary,

    svg: {
      width: 16,
      height: 16,
    },
  },

  ".MuiDataGrid-filler > *": {
    border: 0,
  },

  ".MuiDataGrid-scrollbarFiller": {
    borderBottom: "0 !important",
  },

  // Columns
  //   ".MuiDataGrid-pinnedColumns": {
  //     backgroundColor: "transparent",
  //     backgroundImage: "none",
  //     backdropFilter: "blur(50px)",
  //     ...theme.helpers(theme).maskImage.full,

  //     "&.MuiDataGrid-pinnedColumns--left": {
  //       boxShadow: `2px 0px 8px 0px rgba(${hexToRgbString(theme.palette.background.default)},1)`,
  //     },
  //     "&.MuiDataGrid-pinnedColumns--right": {
  //       boxShadow: `-2px 0px 8px 0px rgba(${hexToRgbString(theme.palette.background.default)},1)`,
  //     },
  //   },

  ".MuiDataGrid-cell--pinnedLeft, .MuiDataGrid-cell--pinnedRight": {
    "&.MuiDataGrid-cell--pinnedLeft": {
      "&:last-of-type": {
        boxShadow: `2px 0px 8px 0px rgba(${hexToRgbString(theme.palette.background.default)},1)`,
      },
    },
    "&.MuiDataGrid-cell--pinnedRight": {
      "&:first-of-type": {
        boxShadow: `-2px 0px 8px 0px rgba(${hexToRgbString(theme.palette.background.default)},1)`,
      },
    },
  },

  // Others
  ".MuiDataGrid-virtualScroller": {
    //border: `1px solid ${theme.palette.backgroundAlpha.border}`,
    borderRadius: "0 0 6px 6px",
    // ...theme.helpers(theme).scrollBar,
    // overflowY: "auto !important",
    "&.draggable": {
      cursor: "grab",
      ".MuiDataGrid-row": {
        cursor: "grab",
      },
      "&:hover": {
        // background: theme.palette.backgroundAlpha.main,
      },
    },

    "&.dragging": {
      cursor: "grabbing",
      userSelect: "none",
      //   background: `${theme.palette.background.paper} !important`,

      ".MuiDataGrid-row": {
        cursor: "grabbing",
      },
    },
  },
  ".MuiDataGrid-footerContainer": {
    border: "none",
  },
  ".table-data-row-group": {
    ...theme.typography.body1,
  },
  ".MuiDataGrid-treeDataGroupingCell": {
    marginLeft: 0,
    span: {
      overflow: "hidden",
      textOverflow: "ellipsis",
    },
  },
  ".MuiDataGrid-treeDataGroupingCellToggle": {
    marginRight: theme.spacing(1),
    flex: "0 0 30px",
  },
  ".MuiDataGrid-row": {
    cursor: "pointer",
    ...theme.typography.body1,
    borderBottom: `1px solid ${theme.palette.divider}`,

    "&.active-data-row": {
      backgroundColor: theme.palette.primary.alpha,

      "&:hover": {
        ".MuiDataGrid-cell--pinnedLeft, .MuiDataGrid-cell--pinnedRight": {
          backgroundColor: theme.palette.primary.dark,
        },
      },

      ".MuiDataGrid-cell--pinnedLeft, .MuiDataGrid-cell--pinnedRight": {
        backgroundColor: theme.palette.primary.dark,
      },
    },
  },
}));

export { _DataGrid as DataGrid };
